import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, LayoutGrid, Table as TableIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataPagination } from '@/components/ui/data-pagination'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useAssessmentAnswers } from '@/features/assessments/hooks'
import { useDebounce } from '@/hooks/use-debounce'
import type { Answer } from '@/features/assessments/types'

interface AnswersTabProps {
  assessmentId: string
}

export function AnswersTab({ assessmentId }: AnswersTabProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table')
  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading, isError } = useAssessmentAnswers(assessmentId, {
    page,
    per_page: 10,
    search: debouncedSearch || undefined,
  })

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600">
          Failed to load answers. Please try again.
        </CardContent>
      </Card>
    )
  }

  const items: Answer[] = data?.items || []
  const totalPages = data?.pages || 1

  return (
    <Card className="flex flex-col h-full border-none shadow-none bg-transparent">
      <CardHeader className="px-0 pt-0 pb-4">
        <div className="flex flex-col gap-4">
          <div>
            <CardTitle>Detailed Answers</CardTitle>
            <CardDescription>
              Review the detailed answers, justifications, and recommendations for each question.
            </CardDescription>
          </div>
          <div className="flex items-center gap-4 bg-gray-50/80 p-3 rounded-lg border border-gray-200 shadow-sm">
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search questions or domains..."
                value={search}
                onChange={handleSearchChange}
                className="pl-9 bg-white"
              />
            </div>
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-200/50 rounded-md p-1">
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 px-2 ${viewMode === 'card' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                onClick={() => setViewMode('card')}
                title="Card View"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 px-2 ${viewMode === 'table' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                <TableIcon className="h-4 w-4" />
              </Button>
            </div>
            {/* Additional filters can be added to this row */}
            <div className="text-sm text-muted-foreground ml-auto pr-2">
              Showing {items.length} result{items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 flex-1 flex flex-col">
        <div className="space-y-6 flex-1">
          <AnimatePresence mode="popLayout">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center py-12"
              >
                <LoadingSpinner />
              </motion.div>
            ) : items.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center py-12 text-muted-foreground bg-white border rounded-lg"
              >
                No answers found matching your search.
              </motion.div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white border rounded-xl shadow-sm overflow-hidden"
              >
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                      <TableHead className="w-[30%]">Question & Domain</TableHead>
                      <TableHead className="w-[10%] text-center">Answer</TableHead>
                      <TableHead className="w-[30%]">Justifications</TableHead>
                      <TableHead className="w-[30%]">Recommendation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((answer, idx) => (
                      <TableRow key={answer.id || `answer-table-${idx}`}>
                        <TableCell className="align-top">
                          <div className="text-sm font-medium text-gray-900 mb-2 leading-snug">
                            {answer.question_text || 'No question text available'}
                          </div>
                          {answer.domain && (
                            <Badge variant="secondary" className="text-[10px] font-medium px-2 py-0">{answer.domain}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="align-top text-center">
                          <span className="inline-flex items-center justify-center bg-gray-100 border border-gray-200 rounded-md px-3 py-1 text-sm font-bold text-gray-900 shadow-inner min-w-[3rem]">
                            {answer.score !== undefined ? (
                              answer.score === 1 ? 'Yes' : answer.score === 0 ? 'No' : answer.score
                            ) : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="align-top">
                          {(() => {
                            const isYes = answer.score !== undefined && answer.score >= 1
                            const isNo = answer.score !== undefined && answer.score === 0
                            if (isYes && answer.justification_for_yes) {
                              return (
                                <div className="space-y-1">
                                  <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Yes
                                  </div>
                                  <div className="bg-green-50/50 border border-green-100 rounded-md p-2 text-xs text-gray-800 leading-relaxed">
                                    {answer.justification_for_yes}
                                  </div>
                                </div>
                              )
                            }
                            if (isNo && answer.justification_for_no) {
                              return (
                                <div className="space-y-1">
                                  <div className="text-[10px] font-bold text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> No - Gap
                                  </div>
                                  <div className="bg-red-50/50 border border-red-100 rounded-md p-2 text-xs text-gray-800 leading-relaxed">
                                    {answer.justification_for_no}
                                  </div>
                                </div>
                              )
                            }
                            return <span className="italic text-gray-400 text-xs">N/A</span>
                          })()}
                        </TableCell>
                        <TableCell className="align-top">
                          {answer.score === 0 && (
                            <div className="space-y-1">
                              <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Recommendation
                              </div>
                              <div className="bg-blue-50/50 border border-blue-100 rounded-md p-2 text-xs text-gray-800 leading-relaxed min-h-[3rem]">
                                {answer.recommendation || <span className="italic text-gray-400">N/A</span>}
                              </div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </motion.div>
            ) : (
              items.map((answer, idx) => (
                <motion.div
                  key={answer.id || `answer-${idx}`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.2, delay: idx * 0.05 }}
                  className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="flex flex-col gap-5">
                    {/* Row 1: Question and Answer */}
                    <div className="flex justify-between items-start gap-4 pb-4 border-b">
                      <div className="flex-1">
                        <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">Question</div>
                        <div className="text-base font-medium text-gray-900 leading-snug">
                          {answer.question_text || 'No question text available'}
                        </div>
                        {answer.domain && (
                          <div className="pt-2.5">
                            <Badge variant="secondary" className="text-xs font-medium px-2.5 py-0.5">{answer.domain}</Badge>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end min-w-[100px] sm:min-w-[120px]">
                        <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">Answer</div>
                        <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 w-full flex items-center justify-center shadow-inner">
                          <span className="text-lg font-bold text-gray-900">
                            {answer.score !== undefined ? (
                              answer.score === 1 ? 'Yes' : answer.score === 0 ? 'No' : answer.score
                            ) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Justification (matching answer only) */}
                    <div>
                      {(() => {
                        const isYes = answer.score !== undefined && answer.score >= 1
                        const isNo = answer.score !== undefined && answer.score === 0
                        if (isYes && answer.justification_for_yes) {
                          return (
                            <div className="flex flex-col space-y-1.5">
                              <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Justification (Yes)
                              </div>
                              <div className="bg-green-50/70 border border-green-200 rounded-lg p-3.5 text-sm text-gray-800 leading-relaxed shadow-sm">
                                {answer.justification_for_yes}
                              </div>
                            </div>
                          )
                        }
                        if (isNo && answer.justification_for_no) {
                          return (
                            <div className="flex flex-col space-y-1.5">
                              <div className="text-[10px] font-bold text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Justification (No - Gap)
                              </div>
                              <div className="bg-red-50/70 border border-red-200 rounded-lg p-3.5 text-sm text-gray-800 leading-relaxed shadow-sm">
                                {answer.justification_for_no}
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div className="flex flex-col space-y-1.5">
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Justification</div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3.5 text-sm italic text-gray-400 shadow-sm">N/A</div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* Row 3: Recommendation */}
                    {answer.score === 0 && (
                      <div className="flex flex-col space-y-1.5">
                        <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Recommendation
                        </div>
                        <div className="bg-blue-50/70 border border-blue-200 rounded-lg p-4 text-sm text-gray-800 leading-relaxed shadow-sm">
                          {answer.recommendation || <span className="italic text-gray-400">N/A</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <DataPagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
